import React, { FC, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
} from '@mui/material';
import { IProductCategory, buildParentOptions } from './constants';
import { AttributeOptionsSelector } from '../../../components/AttributeOptions/AttributeOptionsSelector';

interface Props {
  data?: IProductCategory;
  list: IProductCategory[];
  isLoading?: boolean;
  onSubmit: (payload: Partial<IProductCategory>, attributeValueIds: number[]) => void;
  onClose: () => void;
}

// Create / edit form for a product_categories row. Plain controlled fields —
// only `name` is required; the rest are optional taxonomy metadata.
export const ProductCategoryForm: FC<Props> = ({
  data,
  list,
  isLoading,
  onSubmit,
  onClose,
}) => {
  const [name, setName] = useState(data?.name ?? '');
  const [slug, setSlug] = useState(data?.slug ?? '');
  const [externalId, setExternalId] = useState(data?.external_id ?? '');
  const [order, setOrder] = useState<string>(String(data?.order ?? 0));
  const [parentId, setParentId] = useState<number | ''>(data?.parent_id ?? '');
  const [attributeValueIds, setAttributeValueIds] = useState<number[]>(
    (data?.attribute_values ?? []).map((v) => v.id),
  );
  const [touched, setTouched] = useState(false);

  // A category can't be its own parent (or, simply, we exclude itself from the
  // options). We don't walk the full descendant chain here — the backend tree
  // is shallow and admins pick sensibly; an invalid cycle just won't surface.
  const parentOptions = useMemo(
    () => buildParentOptions(list, data?.id),
    [list, data?.id],
  );

  const submit = () => {
    setTouched(true);
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      slug: slug.trim() || undefined,
      external_id: externalId.trim() || undefined,
      order: Number(order) || 0,
      parent_id: parentId === '' ? null : Number(parentId),
    }, attributeValueIds);
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography fontFamily="CircularStdBlack" sx={{ fontSize: 22 }}>
          {data ? 'Edit' : 'Add'} Category
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col gap-4 pt-2">
          <TextField
            label="Name"
            value={name}
            required
            onChange={(e) => setName(e.target.value)}
            error={touched && !name.trim()}
            helperText={touched && !name.trim() ? 'Name is required' : ''}
            fullWidth
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            helperText="Optional — generated from the name when left blank"
            fullWidth
          />
          <TextField
            label="External ID"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            helperText="Optional — maps to the taxonomy sheet ID"
            fullWidth
          />
          <TextField
            select
            label="Parent Category"
            value={parentId}
            onChange={(e) =>
              setParentId(e.target.value === '' ? '' : Number(e.target.value))
            }
            fullWidth
          >
            <MenuItem value="">— None (top level) —</MenuItem>
            {parentOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            fullWidth
          />
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Attribute options</Typography>
            <AttributeOptionsSelector
              selectedIds={attributeValueIds}
              onChange={setAttributeValueIds}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1.5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={isLoading}>
          {isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
